/**
 * CourierInput.jsx — 택배사 검색/자동완성 입력 컴포넌트
 *
 * 국내 주요 택배사 목록을 상수로 관리.
 * 타이핑 시 필터링된 드롭다운 표시, 직접 입력도 허용(combobox 패턴).
 * 외부 클릭·Escape로 닫힘, Enter로 첫 번째 항목 선택.
 */
import React, { useEffect, useRef, useState } from 'react';

// ── 국내 주요 택배사 목록 ─────────────────────────────────────────────────────
export const COURIER_LIST = [
  'CJ대한통운',
  '한진택배',
  '롯데택배',
  '우체국택배',
  '로젠택배',
  '경동택배',
  '합동택배',
  '대신택배',
  'CVSnet편의점택배',
  '쿠팡로켓배송',
];

// ── 컴포넌트 ──────────────────────────────────────────────────────────────────

export default function CourierInput({ value, onChange, onKeyDown, placeholder = '택배사', style = {}, autoFocus = false }) {
  const [open, setOpen]       = useState(false);
  const [query, setQuery]     = useState(value || '');
  const wrapRef               = useRef(null);
  const inputRef              = useRef(null);

  // 외부에서 value 변경 시 내부 query 동기화
  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // 입력값으로 필터링된 목록
  const filtered = query.trim()
    ? COURIER_LIST.filter((c) => c.toLowerCase().includes(query.trim().toLowerCase()))
    : COURIER_LIST;

  function handleChange(e) {
    const v = e.target.value;
    setQuery(v);
    onChange(v);
    setOpen(true);
  }

  function select(courier) {
    setQuery(courier);
    onChange(courier);
    setOpen(false);
    inputRef.current?.focus();
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') { setOpen(false); }
    if (e.key === 'Enter' && open && filtered.length > 0) {
      e.preventDefault();
      select(filtered[0]);
      return;
    }
    if (e.key === 'ArrowDown' && !open) { setOpen(true); return; }
    onKeyDown?.(e);
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', ...style }}>
      <input
        ref={inputRef}
        autoFocus={autoFocus}
        value={query}
        placeholder={placeholder}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '6px 9px', fontSize: 12,
          border: '1.5px solid var(--border)', borderRadius: 6,
          outline: 'none', background: 'var(--bg-card)',
          transition: 'border-color 0.15s',
        }}
        onFocusCapture={(e) => { e.target.style.borderColor = '#a78bfa'; setOpen(true); }}
        onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
      />
      {/* 드롭다운 */}
      {open && filtered.length > 0 && (
        <ul style={{
          position: 'absolute', top: 'calc(100% + 3px)', left: 0, right: 0,
          background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.11)', margin: 0, padding: '4px 0',
          listStyle: 'none', zIndex: 300, maxHeight: 220, overflowY: 'auto',
        }}>
          {filtered.map((c) => (
            <li
              key={c}
              onMouseDown={(e) => { e.preventDefault(); select(c); }}
              style={{
                padding: '7px 12px', fontSize: 12, cursor: 'pointer', color: 'var(--text-sub)',
                background: c === query ? '#f5f3ff' : 'transparent',
                fontWeight: c === query ? 700 : 400,
                transition: 'background 0.1s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f5f3ff'}
              onMouseLeave={(e) => e.currentTarget.style.background = c === query ? '#f5f3ff' : 'transparent'}
            >
              {c}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
